/**
 * Pure view-model logic for the order-history screens (OrderHistoryPage +
 * OrderDetailPage). No React, no tRPC — testable directly with Vitest.
 *
 * Date/time formatting reuses lib/dateFormat (TZ pinned to Europe/Madrid, never
 * runtime TZ) rather than duplicating the Intl.DateTimeFormat setup.
 */
import type {RouterOutputs} from '../../trpc'
import {deriveOrderStatus} from '@retail/orders-domain'
import type {OrderStatus} from '@retail/orders-domain'
import type {CutoffRule} from '@retail/dates'
import {currentOrderingWeek} from '@retail/dates'
import {formatMadridDate, formatWeekLabel} from '../../lib/dateFormat'

export type OrderListItem = RouterOutputs['orders']['listByStore']['items'][number]
export type OrderDetail = RouterOutputs['orders']['byId']
export type OrderLineData = OrderListItem['products'][number]
export type OrderReturnData = NonNullable<OrderDetail['returns']>[number]

// ---------------------------------------------------------------------------
// List row shaping
// ---------------------------------------------------------------------------

export type OrderHistoryRow = {
  order: OrderListItem
  weekLabel: string
  plates: number
  deliveryLabel: string
  returnsCount: number
}

/** Sum of quantities across order lines (the "nº platos" column). */
export const sumQuantities = (lines: Array<OrderLineData>): number =>
  lines.reduce((sum, line) => sum + line.quantity, 0)

/** "lunes 29 jun · 09:00–14:00" */
export const formatDeliveryLabel = (delivery: OrderListItem['delivery']): string =>
  `${formatMadridDate(delivery.option.deliveryDate)} · ${delivery.option.start}–${delivery.option.end}`

export const toHistoryRow = (order: OrderListItem): OrderHistoryRow => ({
  order,
  weekLabel: formatWeekLabel(order.menuMetadata.isoYear, order.menuMetadata.isoWeek),
  plates: sumQuantities(order.products),
  deliveryLabel: formatDeliveryLabel(order.delivery),
  returnsCount: order.returns?.length ?? 0,
})

export const toHistoryRows = (items: Array<OrderListItem>): Array<OrderHistoryRow> =>
  items.map(toHistoryRow)

// ---------------------------------------------------------------------------
// "Editar pedido" gating
// ---------------------------------------------------------------------------

/**
 * Whether the detail's "Editar pedido" CTA should be offered for this order.
 *
 * The CTA opens the proposal editor, which ALWAYS targets the ordering week
 * (currentOrderingWeek — the week whose cutoff is the next to come); it can't
 * open any other week. So the CTA is only actionable for that week's order.
 * An order that is editable purely by cutoff but belongs to any other week
 * would dead-end on the wrong week, so it stays read-only here. Orders of
 * past weeks are CLOSED and excluded by the status check.
 *
 * The ordering week is the STORE's — resolved with its own cutoff rule when it
 * has one — because that is the week the editor opens for this store.
 */
export const isEditableOrderingWeek = (
  order: {cutoffAt: Date; menuMetadata: {isoYear: number; isoWeek: number}},
  now: Date,
  cutoff?: CutoffRule,
): boolean => {
  const {week, year} = currentOrderingWeek(now, cutoff)
  return (
    deriveOrderStatus(order, now) === 'EDITABLE' &&
    order.menuMetadata.isoYear === year &&
    order.menuMetadata.isoWeek === week
  )
}

// ---------------------------------------------------------------------------
// Pagination
// ---------------------------------------------------------------------------

/** Default page size — mirrors the backend's zod default for listByStore. */
export const DEFAULT_PAGE_LIMIT = 20

// The page range + prev/next enable logic lives in the kit's <Pagination>
// (OrderHistoryPage wires it directly); no view-model helpers are needed here.

// ---------------------------------------------------------------------------
// Detail — returns
// ---------------------------------------------------------------------------

const RETURN_REASON_LABELS: Record<string, string> = {
  NOT_DELIVERED: 'No entregado',
  DEFECTIVE: 'Defectuoso',
  OTHER: 'Otro',
}

export const formatReturnReason = (reason: string | undefined): string =>
  reason !== undefined ? (RETURN_REASON_LABELS[reason] ?? reason) : 'Sin motivo indicado'

/**
 * An order line with its product name already resolved. The name comes from the
 * client's catalog, which the order itself does not carry, so every shaping
 * function below takes a resolver rather than reaching for a query.
 */
export type NamedOrderLine = {
  productId: number
  productName: string
  quantity: number
}

/** Resolves a productId to a display name; `#<id>` when the catalog has none. */
export type ResolveProductName = (productId: number) => string

export const toNamedLines = (
  lines: Array<OrderLineData>,
  resolveProductName: ResolveProductName,
): Array<NamedOrderLine> =>
  lines.map((line) => ({
    productId: line.productId,
    productName: resolveProductName(line.productId),
    quantity: line.quantity,
  }))

export type ReturnView = {
  serialNumber: number
  dateLabel: string
  reasonLabel: string
  notes?: string
  lines: Array<NamedOrderLine>
  totalQuantity: number
}

export const toReturnView = (
  ret: OrderReturnData,
  resolveProductName: ResolveProductName,
): ReturnView => ({
  serialNumber: ret.serialNumber,
  dateLabel: formatMadridDate(ret.date),
  reasonLabel: formatReturnReason(ret.reason),
  notes: ret.notes,
  lines: toNamedLines(ret.products, resolveProductName),
  totalQuantity: sumQuantities(ret.products),
})

export const toReturnViews = (
  returns: Array<OrderReturnData> | undefined,
  resolveProductName: ResolveProductName,
): Array<ReturnView> =>
  (returns ?? []).map((ret) => toReturnView(ret, resolveProductName))

// ---------------------------------------------------------------------------
// Detail — the whole screen, shaped
// ---------------------------------------------------------------------------

export type OrderDetailView = {
  weekLabel: string
  status: OrderStatus
  /**
   * Whether to offer the "Editar pedido" CTA — see isEditableOrderingWeek: the
   * proposal editor only ever opens the ORDERING week.
   */
  isEditable: boolean
  cutoffAt: Date
  lines: Array<NamedOrderLine>
  totalUnits: number
  deliveryLabel: string
  address: OrderDetail['delivery']['address']
  returns: Array<ReturnView>
}

export const toOrderDetailView = (
  order: OrderDetail,
  resolveProductName: ResolveProductName,
  now: Date,
  /** The store's own cutoff rule, for the ordering-week check; absent = the general one. */
  cutoff?: CutoffRule,
): OrderDetailView => ({
  weekLabel: formatWeekLabel(order.menuMetadata.isoYear, order.menuMetadata.isoWeek),
  status: deriveOrderStatus(order, now),
  isEditable: isEditableOrderingWeek(order, now, cutoff),
  cutoffAt: order.cutoffAt,
  lines: toNamedLines(order.products, resolveProductName),
  totalUnits: sumQuantities(order.products),
  deliveryLabel: formatDeliveryLabel(order.delivery),
  address: order.delivery.address,
  returns: toReturnViews(order.returns, resolveProductName),
})
